import { Button, Divider, Flex } from '@flex-design-system/fx';
import { SendIcon } from '@flex-design-system/legacy-flex-icons';
import { styled } from '@flex-design-system/stitches-react';
import { Translation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import { isNonNil } from '@flex-packages/utils';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form';

import type { PublicJobDescriptionDetail } from '../../../../base';
import { StaticUrlPagePath } from '../../../../base';
import { parseToInternalRelativeUrl } from '../../logics';

import type {
  BackgroundSectionHandle,
  ExcludedSensitiveInformationItem,
} from './components';
import {
  ApplicationPathField,
  AttachmentDocumentField,
  ConfirmApplyDialog,
  DisabledTypeField,
  EducationalBackgroundSection,
  EmailField,
  MilitaryServiceField,
  NameField,
  PhoneNumberField,
  PreQuestionField,
  SubmittedModal,
  Terms,
  VeteranField,
  WorkExperienceBackgroundSection,
} from './components';
import { useSubmitJobDescriptionApplyForm } from './hooks';
import type { JobDescriptionFormState } from './models';
import { SessionStorageAutoSaver } from './session-storage';
import { FormSection } from './shared';

interface Props {
  customerIdHash: string;
  customerName: string;
  jobDescription: PublicJobDescriptionDetail;
}

const BASIC_FIELD_FOCUS_ORDER = [
  'name',
  'email',
  'phoneNumber',
  'militaryServiceStatus',
  'handicapCase',
  'hasVeteransCompensation',
  'applicationPath',
] satisfies ReadonlyArray<keyof JobDescriptionFormState>;

const findFirstErroredIndex = (itemErrors: unknown): number => {
  if (!Array.isArray(itemErrors)) {
    return 0;
  }
  const index = itemErrors.findIndex(isNonNil);
  return index < 0 ? 0 : index;
};

export function JobDescriptionForm({
  customerIdHash,
  customerName,
  jobDescription,
}: Props) {
  const { additionalInfo } = jobDescription;

  const [showDialog, setShowDialog] = useState(false);
  const [isSubmitSuccess, setSubmitSuccess] = useState(false);
  const [
    excludedSensitiveInformationItems,
    setExcludedSensitiveInformationItems,
  ] = useState<ExcludedSensitiveInformationItem[]>([]);

  const { getDefaultValue, clear } = SessionStorageAutoSaver.use({
    jobDescription,
  });
  const { mutateAsync: submit, isLoading: isSubmitting } =
    useSubmitJobDescriptionApplyForm({
      jobDescription,
    });

  const educationSectionRef = useRef<BackgroundSectionHandle>(null);
  const workExperienceSectionRef = useRef<BackgroundSectionHandle>(null);

  const formMethods = useForm<JobDescriptionFormState>({
    defaultValues: getDefaultValue(),
    reValidateMode: 'onChange',
    shouldFocusError: false,
  });

  const handleSubmitButtonClick = () => {
    const {
      agreedToSensitiveInformationTerms,
      hasVeteransCompensation,
      handicapCase,
    } = formMethods.getValues();

    // NOTE: 민감정보 미동의 + 민감정보(장애·보훈) 입력값 있음 → 지원 시 해당 항목이 제출에서 제외됨을 confirm 으로 안내한다 (EPDIV-1271, EPDIV-1572).
    const excludedItems: ExcludedSensitiveInformationItem[] = [];
    if (agreedToSensitiveInformationTerms === false) {
      if (isNonNil(handicapCase)) {
        excludedItems.push('handicap');
      }
      if (isNonNil(hasVeteransCompensation)) {
        excludedItems.push('veteran');
      }
    }
    setExcludedSensitiveInformationItems(excludedItems);
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      await submit(formMethods.getValues());
      setShowDialog(false);
      setSubmitSuccess(true);
      clear();
    } catch (e) {
      console.error(e);
    }
  };

  const scrollToFirstError = (errors: FieldErrors<JobDescriptionFormState>) => {
    const erroredBasicField = BASIC_FIELD_FOCUS_ORDER.find(field => {
      return isNonNil(errors[field]);
    });
    const educationErroredIndex = errors.educationalBackgrounds
      ? findFirstErroredIndex(errors.educationalBackgrounds)
      : undefined;
    const workErroredIndex = errors.workExperienceBackgrounds
      ? findFirstErroredIndex(errors.workExperienceBackgrounds)
      : undefined;

    // 스크롤은 문서 순서상 최상단 에러 하나로만 (기본 필드 > 학력 > 경력).
    const scrollTarget = erroredBasicField
      ? 'basic'
      : educationErroredIndex !== undefined
        ? 'education'
        : workErroredIndex !== undefined
          ? 'work'
          : undefined;

    // 에러난 학력·경력 항목은 모두 펼치고 검증한다(스크롤은 최상단만).
    if (educationErroredIndex !== undefined) {
      educationSectionRef.current?.revealError(
        educationErroredIndex,
        scrollTarget === 'education'
      );
    }
    if (workErroredIndex !== undefined) {
      workExperienceSectionRef.current?.revealError(
        workErroredIndex,
        scrollTarget === 'work'
      );
    }
    if (scrollTarget === 'basic' && erroredBasicField) {
      formMethods.setFocus(erroredBasicField);
    }
  };

  const formOnSubmit = formMethods.handleSubmit(
    handleSubmitButtonClick,
    scrollToFirstError
  );
  const requiredSensitiveInformation =
    additionalInfo.veteran.visible || additionalInfo.handicapCase.visible;

  useEffect(() => {
    const { educationalBackgrounds, workExperienceBackgrounds } =
      formMethods.getValues();

    if (additionalInfo.education.visible && !educationalBackgrounds?.length) {
      formMethods.setValue('educationalBackgrounds', [{} as any]);
    }
    if (additionalInfo.work.visible && !workExperienceBackgrounds?.length) {
      formMethods.setValue('workExperienceBackgrounds', [{} as any]);
    }
  }, [
    additionalInfo.education.visible,
    additionalInfo.work.visible,
    formMethods,
  ]);

  return (
    <FormProvider {...formMethods}>
      <SessionStorageAutoSaver
        jobDescriptionIdHash={jobDescription.jobDescriptionIdHash}
      />
      <form noValidate onSubmit={formOnSubmit}>
        <Wrapper direction="column" align="stretch">
          <Flex direction="column" align="stretch" css={{ gap: 70 }}>
            <FormSection
              title={
                <FormSection.Title>
                  <Translation
                    tKey={trans(
                      'recruiting.site_builder.job_description.apply.basic_info'
                    )}
                  />
                </FormSection.Title>
              }
            >
              <NameField />
              <EmailField />
              <PhoneNumberField />
              {additionalInfo.militaryService.visible ? (
                <MilitaryServiceField
                  required={additionalInfo.militaryService.required}
                />
              ) : null}
              {additionalInfo.handicapCase.visible ? (
                <DisabledTypeField
                  required={additionalInfo.handicapCase.required}
                />
              ) : null}
              {additionalInfo.veteran.visible ? (
                <VeteranField required={additionalInfo.veteran.required} />
              ) : null}
              <ApplicationPathField customerIdHash={customerIdHash} />
            </FormSection>
            {additionalInfo.education.visible ? (
              <EducationalBackgroundSection
                ref={educationSectionRef}
                required={additionalInfo.education.required}
              />
            ) : null}
            {additionalInfo.work.visible ? (
              <WorkExperienceBackgroundSection
                ref={workExperienceSectionRef}
                required={additionalInfo.work.required}
              />
            ) : null}
            {jobDescription.documentList.length > 0 ? (
              <FormSection
                title={
                  <FormSection.Title>
                    <Translation
                      tKey={trans(
                        'recruiting.site_builder.job_description.apply.attachment'
                      )}
                    />
                  </FormSection.Title>
                }
              >
                <AttachmentDocumentField />
              </FormSection>
            ) : null}
            {jobDescription.preQuestionList.length > 0 ? (
              <Flex direction="column" align="stretch" css={{ gap: 40 }}>
                {jobDescription.preQuestionList.map((preQuestion, index) => {
                  return (
                    <PreQuestionField
                      key={preQuestion.idHash}
                      preQuestion={preQuestion}
                      expectedMaxContentLength={
                        preQuestion.expectedMaxContentLength
                      }
                      index={index}
                    />
                  );
                })}
              </Flex>
            ) : null}
          </Flex>
          <Divider color="blackAlpha010" css={{ my: 60 }} />
          <Flex direction="column" align="stretch" css={{ gap: 34 }}>
            <Terms
              customerName={customerName}
              requiredSensitiveInformation={requiredSensitiveInformation}
            />
            <Button
              type="submit"
              size="xlarge"
              color="grayDarker"
              leftSlot={<SendIcon />}
              css={{ marginTop: 68 }}
            >
              <Translation
                tKey={trans(
                  'recruiting.site_builder.job_description.apply.short'
                )}
              />
            </Button>
          </Flex>
          <ConfirmApplyDialog
            open={showDialog}
            onOpenChange={setShowDialog}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            excludedSensitiveInformationItems={
              excludedSensitiveInformationItems
            }
          />
          <SubmittedModal
            open={isSubmitSuccess}
            mainPageLink={parseToInternalRelativeUrl({
              urlPagePath: StaticUrlPagePath.Main,
            })}
          />
        </Wrapper>
      </form>
    </FormProvider>
  );
}

const Wrapper = styled(Flex, {
  paddingTop: 60,
  paddingBottom: 100,
});
